import { action } from 'easy-peasy';

const gameModel = {
  // Lobby
  players: [],
  addPlayer: action((state, payload) => {
    if (!state.players.find(p => p.id === payload.id)) {
      state.players.push(payload);
    }
  }),
  removePlayer: action((state, payload) => {
    state.players = state.players.filter(p => p.id !== payload.id);
  }),
  updatePlayer: action((state, payload) => {
    const p = state.players.find(p => p.id === payload.id);
    if (p) p.username = payload.username;
  }),

  // Game state from server
  phase: 'LOBBY',
  setPhase: action((state, payload) => { state.phase = payload; }),

  // Which quadrant face sits in which corner, indexed by CORNERS from
  // board.mjs. Server-owned; null until the first GAME_STATE lands.
  arrangement: null,
  setArrangement: action((state, payload) => { state.arrangement = payload; }),

  boardState: {},     // { [color]: { x, y } }
  setBoardState: action((state, payload) => { state.boardState = payload; }),
  // Single-robot update for ROBOT_MOVED/MOVE_UNDONE. Mutating the draft reads
  // the latest board even when two socket events land before a re-render —
  // easy-peasy actions cannot take functional updaters (see AGENTS.md), so a
  // spread-merge at the call site would race and silently drop a move.
  applyRobotPosition: action((state, payload) => {
    if (state.boardState[payload.color]) {
      state.boardState[payload.color] = payload.to;
    }
  }),

  activeMission: null,
  setActiveMission: action((state, payload) => { state.activeMission = payload; }),

  // Optimal move count for the active mission, announced by the server when
  // settings.revealOptimal is on. Null means the mode does not reveal it, or
  // the solver has not answered yet. Only ever a number — the server never
  // sends the moves themselves.
  optimalMoves: null,
  setOptimalMoves: action((state, payload) => { state.optimalMoves = payload; }),

  // The last hint this player was given, `{ step, total, color }`. Cleared on
  // every phase change — a hint names a robot for one round's opening board.
  hint: null,
  setHint: action((state, payload) => { state.hint = payload; }),

  bids: [],           // [{ playerId, username, count }]
  setBids: action((state, payload) => { state.bids = payload; }),
  addBid: action((state, payload) => {
    state.bids = state.bids.filter(b => b.playerId !== payload.playerId);
    state.bids.push(payload);
    state.bids.sort((a, b) => a.count - b.count);
  }),

  scores: {},         // { [playerId]: number }
  setScores: action((state, payload) => { state.scores = payload; }),

  demoPlayerId: null,
  setDemoPlayerId: action((state, payload) => { state.demoPlayerId = payload; }),

  demoMovesRemaining: 0,
  setDemoMovesRemaining: action((state, payload) => { state.demoMovesRemaining = payload; }),

  // The turn's full move budget, kept alongside the remaining count so DemoBar
  // can render "n of m" and the pip row. Remaining decrements; this does not.
  demoMovesAllowed: 0,
  setDemoMovesAllowed: action((state, payload) => { state.demoMovesAllowed = payload; }),

  biddingTimer: null, // seconds remaining
  setBiddingTimer: action((state, payload) => { state.biddingTimer = payload; }),

  // Seconds left in the current demo turn (DEMO_TIMER_TICK). null outside DEMO
  // and until the first tick lands, which is what hides the gauge.
  demoTimer: null,
  setDemoTimer: action((state, payload) => { state.demoTimer = payload; }),

  readyToSkip: [],
  setReadyToSkip: action((state, payload) => { state.readyToSkip = payload; }),

  // Difficulty preset chosen by the host in the lobby; settings are the
  // server-resolved values (never trusted from the client).
  mode: 'normal',
  setMode: action((state, payload) => { state.mode = payload; }),
  settings: null,
  setSettings: action((state, payload) => { state.settings = payload; }),

  selfId: null,
  setSelfId: action((state, payload) => { state.selfId = payload; }),

  hostId: null,
  setHostId: action((state, payload) => { state.hostId = payload; }),
};

export default gameModel;
